import { Component, Input, OnInit, inject } from '@angular/core';
import { combineLatest, Observable, Subject, of } from 'rxjs';
import { map, switchMap, shareReplay } from 'rxjs/operators';
import { Tenant, MaintenanceWindow } from './tenant';
import { MaintenanceService } from './maintenance.service';
import * as moment from 'moment';
import { Solution, Account } from '@aveva/connect-web-core';
import { AccountService } from '../shared/account.service';
import { SolutionsService } from '../shared/solutions.service';

@Component({
  selector: 'app-maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss']
})
export class MaintenanceComponent implements OnInit {
  private maintenanceService = inject(MaintenanceService);
  private accountService = inject(AccountService);
  private solutionService = inject(SolutionsService);

  columnsToDisplay = [
    'connectAccountName',
    'AUnique',
    'connectFolderName',
    'connectFolderRegion',
    'maintenanceStartTime',
    'maintenanceEndTime',
    'maintenanceNotes',
    'remove'
  ];

  private _accountFilter = '';
  private errorMessageSubject = new Subject<string>();
  errorMessage$ = this.errorMessageSubject.asObservable();
  filteredMaintenances$: Observable<Tenant[]>;
  private allMaintenances$: Observable<Tenant[]>;
  accounts$: Observable<Account[]>;
  solutions$: Observable<Solution[]>;
  maintenances$: Observable<Tenant[]>;
  accountIds: string;
  solutionIds: string;
  folder: Solution;
  folderName: string;
  folderRegion: string;


  get accountFilter(): string {
    return this._accountFilter;
  }

  set accountFilter(value: string) {
    this._accountFilter = value;
    if (value) {
      this.filteredMaintenances$ = this.allMaintenances$.pipe(
        map(maintenances =>
          maintenances.filter(maintenance =>
            maintenance.connectAccountName.toLocaleLowerCase().includes(value.toLocaleLowerCase())
          )
        )
      );
    } else {
      this.filteredMaintenances$ = this.allMaintenances$;
    }
  }

  @Input()
  get removeIcon(): string { return this._removeIcon; }
  set removeIcon(value: string) {
    this._removeIcon = value;
  }
  _removeIcon = 'delete';

  @Input()
  get removeTooltip(): string { return this._removeTooltip; }
  set removeTooltip(value: string) {
    this._removeTooltip = value;
  }
  _removeTooltip = 'Delete Maintenance';

  ngOnInit(): void {
    this.allMaintenances$ = this.maintenanceService.getMaintenances().pipe(
      switchMap((maintenances) => {
        this.accounts$ = this.accountService.queryAccounts(
          [...new Set(maintenances.map(maintenance => maintenance.connectAccountId))].toString()
        );
        this.solutions$ = this.solutionService.querySolutions(
          [...new Set(maintenances.map(maintenance => maintenance.connectSolutionId))].toString()
        );
        return this.prepareViewModel(maintenances);
      }),
      shareReplay(1)
    );
    this.filteredMaintenances$ = this.allMaintenances$;
  }

  private prepareViewModel(maintenances: Tenant[]) {
    return combineLatest([of(maintenances), this.accounts$, this.solutions$]).pipe(
      map(([maintenances, accounts, solutions]) =>
        maintenances
          .filter((maintenance: Tenant) => {
            this.folder = solutions.find((solution) => maintenance.connectSolutionId === solution.id);
            this.folderName = (typeof this.folder !== 'undefined') ? this.folder.name : '';
            this.folderRegion = (typeof this.folder !== 'undefined') ? this.folder.region : '';
            return this.folderName !== '' && this.folderRegion !== '';
          })
          .map((maintenance: Tenant) => {
            const activeMaintenanceWindow = this.getActiveMaintenanceWindow(maintenance);
            const connectAccount = accounts.find((account) => maintenance.connectAccountId === account.id);
            const connectAccountName = (typeof connectAccount !== 'undefined') ? connectAccount.name : '';
            this.folder = solutions.find((solution) => maintenance.connectSolutionId === solution.id);
            this.folderName = (typeof this.folder !== 'undefined') ? this.folder.name : '';
            this.folderRegion = (typeof this.folder !== 'undefined') ? this.folder.region : '';

            const infrastructureTenantId = maintenance.infrastructureTenantId || '';
            const aUnique = infrastructureTenantId.split('/')[0];

            return {
              ...maintenance,
              connectAccountName: connectAccountName,
              connectFolderName: (maintenance.connectContextId === maintenance.connectAccountId) ? '' : this.folderName,
              connectFolderRegion: (maintenance.connectContextId === maintenance.connectAccountId) ? '' : this.folderRegion,
              maintenanceStartTime: (activeMaintenanceWindow) ? activeMaintenanceWindow.startTimeUTC : '',
              maintenanceEndTime: (activeMaintenanceWindow) ? activeMaintenanceWindow.endTimeUTC : '',
              maintenanceNotes: (activeMaintenanceWindow) ? activeMaintenanceWindow.description : '',
              aUnique: aUnique
            } as Tenant;
          })
      )
    );
  }

  private getActiveMaintenanceWindow(tenant: Tenant) {
    if (tenant !== null && tenant.maintenanceWindows != null) {
      return tenant.maintenanceWindows.find((maintenanceWindow: MaintenanceWindow) => {
        if (maintenanceWindow.isArchived === false) {
          const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
          const currentDateTime = moment(new Date(), DATE_TIME_FORMAT);
          return (moment(currentDateTime) < moment(maintenanceWindow.endTimeUTC));
        }
        return false;
      });
    }
    return null;
  }

  removeMaintenance(solutionId: string) {
    this.maintenanceService.deleteMaintenance(solutionId).subscribe(() => {
      this.ngOnInit();
    });
  }

}
